<template>
  <div
    :class="[
      'left-sidebar',
      sidebarStore.isOpen ? 'left-sidebar--open' : 'left-sidebar--close',
    ]"
  >
    <div class="main-menu">
      <ul class="list-items" role="menu">
        <li
          v-for="route in filteredRoutes"
          :key="route.name"
          class="main-route"
        >
          <RouterLink
            v-if="!('children' in route)"
            :to="{ name: route.name }"
            :class="[route.active ? 'active' : '']"
            v-tooltip.right="sidebarStore.isOpen ? '' : route.title"
          >
            <span :class="['pi', route.icon]"></span>
            <div v-if="sidebarStore.isOpen">{{ route.title }}</div>
          </RouterLink>
          <div class="parent" v-else>
            <div
              class="name"
              @click="
                route.expanded = sidebarStore.isOpen ? !route.expanded : true;
                onParentRouteClick();
              "
              :class="[route.active ? 'active' : '']"
              v-tooltip.right="sidebarStore.isOpen ? '' : route.title"
            >
              <span :class="['pi', route.icon]"></span>
              <div v-if="sidebarStore.isOpen">{{ route.title }}</div>
            </div>

            <ul
              v-if="sidebarStore.isOpen"
              class="childs"
              :class="route.expanded ? 'visible' : 'hidden'"
            >
              <li v-for="child in route.children" :key="child.name">
                <template v-if="child.name.length">
                  <RouterLink
                    :to="child.to || { name: child.name }"
                    :class="[child.active ? 'active' : '']"
                  >
                    <div>{{ child.title }}</div>
                  </RouterLink>
                </template>
                <template v-else>
                  <div class="mid-parent">{{ child.title }}</div>
                  <ul class="childs visible">
                    <li
                      v-for="childThird in child.children"
                      :key="childThird.name"
                    >
                      <RouterLink
                        :to="childThird.to || { name: childThird.name }"
                        :class="[childThird.active ? 'active' : '']"
                      >
                        <div>{{ childThird.title }}</div>
                      </RouterLink>
                    </li>
                  </ul>
                </template>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>

    <div class="bottom-menu">
      <ul class="list-items" role="menu">
        <li class="main-route">
          <RouterLink
            to="/training"
            :class="[route.name === 'training' ? 'active' : '']"
            v-tooltip.right="sidebarStore.isOpen ? '' : 'Обучение'"
          >
            <span class="pi pi-question-circle"></span>
            <div v-if="sidebarStore.isOpen">Обучение</div>
          </RouterLink>
        </li>
        <li class="main-route">
          <button
            @click="onSideBarClick"
            class="hide-menu-button"
            v-tooltip.right="sidebarStore.isOpen ? '' : 'Раскрыть меню'"
          >
            <span
              :class="[
                'pi',
                sidebarStore.isOpen ? 'pi-angle-left' : 'pi-angle-right',
              ]"
            />
            <div v-if="sidebarStore.isOpen">Скрыть меню</div>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";

import { useRoute } from "vue-router";
import { useAuthStore } from "@/store/auth/login";
import { extractBeforeColon } from "@/services/utils/Strings";
import { useSidebarStore } from "@/store/sidebar/sidebar";
import { rootGetters } from "@/main";
import { useOwnWorkbooksStore } from "@/store/catalog_data/dashboards";
import { Workbook } from "@/store/analytics/dasboards";

const route = useRoute();
const authStore = useAuthStore();
const filteredRoutes = ref<ParentRoute[]>([]);
const sidebarStore = useSidebarStore();
const workbooksStore = useOwnWorkbooksStore();

const onSideBarClick = () => {
  sidebarStore.switchState();
};

const onParentRouteClick = () => {
  sidebarStore.openSidebar();
};

onMounted(async () => {
  await filterRoutes();
  setActiveRoute(filteredRoutes.value);
});

watch(
  () => route.fullPath,
  () => {
    setActiveRoute(filteredRoutes.value);
  }
);

const filterRoutes = async () => {
  if (!authStore.user && authStore.isLoggedIn) {
    await authStore.getUserInfo();
  }

  const isDemoUser = Boolean(
    authStore.user?.role?.department?.organization?.is_demo
  );

  filteredRoutes.value = JSON.parse(JSON.stringify(buildRoutes(isDemoUser)));

  const payload = authStore.getPayload();
  if (payload && payload.scopes) {
    const permissions = payload.scopes
      .map(extractBeforeColon)
      .concat(payload.scopes);
    if (permissions.includes("system") || payload.user.is_superuser) return;
    filteredRoutes.value = filterRoutesByScopes(
      filteredRoutes.value,
      permissions
    );
  }

  if (isDemoUser) {
    await attachDemoWorkbookRoutes();
  }
  return [];
};

const attachDemoWorkbookRoutes = async () => {
  await workbooksStore.getItems("", [], true, false, 0, 50, true);

  const analyticsRoute = filteredRoutes.value.find(
    (routeItem) => routeItem.path === "/analytics"
  );
  if (!analyticsRoute?.children) return;

  const demoWorkbookRoutes: ParentRoute[] = workbooksStore.itemList
    .filter((item: Workbook) => item.dashboards && item.dashboards.length > 0)
    .map((item: Workbook) => ({
      name: `demo-dashboard-${item.id}`,
      title: item.dashboards[0]?.name || item.name,
      active: false,
      path: `/analytics/demo/${item.id}`,
      scope: "analytics",
      to: {
        name: "demoDashboards",
        params: { workbookId: item.id },
      },
    }));

  analyticsRoute.children = analyticsRoute.children.concat(demoWorkbookRoutes);
};

function filterRoutesByScopes(
  routes: ParentRoute[],
  scopes: string[]
): ParentRoute[] {
  return routes
    .filter((route) => !route.hidden)
    .filter((route) => !route.scope || scopes.includes(route.scope))
    .filter((route) => !route.external || scopes.includes("external_agency"))
    .filter((route) => {
      if (route.inhouse) {
        const isInhouseUser =
          authStore.getPayloadTenant() === rootGetters.inhouse_tenant;
        return isInhouseUser;
      }
      return true;
    })
    .map((route) => {
      if (route.children) {
        const filteredChildren = filterRoutesByScopes(route.children, scopes);
        return { ...route, children: filteredChildren };
      }
      return route;
    });
}

const setActiveRoute = (routes: any) => {
  const markActiveRoutes = (routeList: ParentRoute[]) => {
    routeList.forEach((routeInSidebar: ParentRoute) => {
      const active = route.fullPath.includes(routeInSidebar.path);
      routeInSidebar.active = active;

      if (routeInSidebar.children) {
        routeInSidebar.expanded = active;
        markActiveRoutes(routeInSidebar.children);
      }
    });
  };

  markActiveRoutes(routes);
};

const buildRoutes = (isDemo = false): ParentRoute[] => [
  {
    name: "listSegments",
    title: "Сегменты",
    icon: "pi-database",
    active: false,
    path: "/segments",
    scope: "segment",
  },
  {
    name: "listCampaigns",
    title: "Кампании",
    icon: "pi-briefcase",
    active: false,
    path: "/campaigns",
    scope: "campaign",
  },
  {
    name: "settings",
    title: "Настройки",
    icon: "pi-cog",
    active: false,
    path: "/settings",
    scope: "settings",
  },
  {
    name: "",
    title: "Справочники",
    icon: "pi-book",
    active: false,
    expanded: false,
    path: "/dictionaries",
    scope: "dictionaries",
    children: [
      {
        name: "listOrganizations",
        title: "Организации",
        active: false,
        path: "/organizations",
        scope: "organization",
      },
      {
        name: "users",
        title: "Пользователи",
        active: false,
        path: "/users",
        scope: "users",
      },
      {
        name: "brands",
        title: "Бренды",
        active: false,
        path: "/brands",
      },
      {
        name: "shops",
        title: "Магазины",
        active: false,
        path: "/shops",
      },
      {
        name: "adsCabinets",
        title: "Рекламные кабинеты",
        active: false,
        path: "/ads-cabinets",
      },
      {
        name: "platforms",
        title: "Рекламные площадки",
        active: false,
        path: "/platforms",
      },
      {
        name: "estimationParams",
        title: "Параметры эстимации",
        active: false,
        path: "/estimation-params",
        scope: "analytics:edit",
      },
      {
        name: "workbooks",
        title: "Воркбуки",
        active: false,
        path: "/workbooks",
        scope: "analytics",
      },
    ],
  },
  {
    name: "",
    title: "Аналитика",
    icon: "pi-chart-line",
    active: false,
    expanded: false,
    path: "/analytics",
    scope: "analytics",
    children: [
      {
        name: "",
        title: "Кастомизация",
        active: false,
        path: "",
        scope: "analytics:edit",
        children: [
          {
            name: "traficPlanList",
            title: "План трафика",
            active: false,
            path: "/trafic-plan",
            scope: "analytics:edit",
          },
          {
            name: "estimationSettings",
            title: "Эстимация",
            active: false,
            path: "/estimation-settings",
            scope: "analytics:edit",
          },
        ],
      },
      {
        name: "dashboards",
        title: "Дашборды",
        active: false,
        path: "/dashboards",
        scope: "analytics",
        hidden: isDemo,
      },
      {
        name: "clientDashboards",
        title: "Клиентские отчёты",
        active: false,
        path: "/client-dashboards",
        scope: "analytics",
        external: true,
        hidden: isDemo,
      },
      {
        name: "preSale",
        title: "Pre-sale",
        active: false,
        path: "/pre-sale",
        scope: "analytics",
        inhouse: true,
        hidden: isDemo,
      },
    ],
  },
  {
    name: "",
    title: "Расходы",
    icon: "pi-wallet",
    active: false,
    expanded: false,
    path: "/expenses",
    scope: "expenses",
    children: [
      {
        name: "expensesStatistics",
        title: "Статистика",
        active: false,
        path: "/expenses/statistics",
        scope: "expenses",
      },
      {
        name: "coefficients",
        title: "Коэффициенты",
        active: false,
        path: "/coefficients",
        scope: "expenses:edit",
      },
    ],
  },
];

interface ParentRoute {
  name: string;
  title: string;
  icon?: string;
  path: string;
  active: boolean;
  expanded?: boolean;
  scope?: string;
  children?: ParentRoute[];
  external?: boolean;
  inhouse?: boolean;
  hidden?: boolean;
  to?: string | object;
}
</script>

<style lang="scss" scoped>
.left-sidebar {
  transition: width 0.3s ease; // Анимируем ширину и отступы

  position: fixed;
  top: 72px;
  bottom: 12px;
  left: 12px;

  background-color: white;
  border-radius: 8px;

  padding: 8px 10px;
  z-index: 1000;

  width: 64px;

  display: flex;
  flex-direction: column;

  &--open,
  &--closed {
    @extend .left-sidebar;
  }

  &--open {
    width: 220px;

    ul.list-items {
      li.main-route {
        span {
          padding-right: 8px;
        }
      }
    }
  }

  .main-menu {
    flex: 1;
    overflow-y: auto;
  }

  .bottom-menu {
    margin-top: auto;
    padding-top: 10px;
    border-top: 1px solid #f0f0f0;

    .hide-menu-button {
      width: 100%;
      text-align: left;
      justify-content: flex-start;

      &:hover {
        background-color: #f1eff3;
        color: $main-button-color;
      }
    }
  }

  &--closed {
    ul.list-items {
      li.main-route {
        span {
          padding-right: 0;
        }
      }
    }
  }

  ul.list-items {
    padding: 0;
    margin: 0;

    li.main-route {
      list-style-type: none;

      > button {
        border: 0;
        background: none;
        cursor: pointer;
        font-family: $main-font-family;
        font-size: 1rem;
      }

      > a,
      button,
      .parent .name {
        display: flex;
        align-items: center;
        height: 40px;

        text-wrap: nowrap;

        color: $main-font-color;
        border-radius: 6px;
        font-size: 1rem;

        padding: 8px 16px;
        margin-bottom: 10px;

        &.active {
          background-color: #f1eff3;
        }

        &:hover {
          background-color: #f1eff3;
          color: $main-button-color;
        }
      }

      .parent .name {
        cursor: pointer;
      }
    }
  }

  ul.childs {
    margin-top: -2px;
    margin-left: 18px;
    padding: 0;
    display: none;

    li {
      list-style-type: none;
      margin-bottom: 8px;

      a {
        border-radius: 6px;
        display: block;
        padding-left: 8px;
        color: $main-font-color;
        line-height: 20px;
        padding-top: 4px;
        padding-bottom: 4px;

        &:hover {
          background-color: #eaeaf0;
        }

        &.active {
          background-color: #f1eff3;
        }
      }
    }

    &.visible {
      display: block;
    }
  }

  .mid-parent {
    padding-top: 4px;
    padding-left: 8px;
    margin-bottom: 18px;
  }
}
</style>
